import { Popover } from 'antd';
import TcButton from 'components/UI/button/TcButton';
import { isAnySubMenusSelected, isMenuSelected } from 'components/UI/layout/components/dashboardSidebar/sidebarFunctions';
import TcTooltip from 'components/UI/Tooltip/TcTooltip';
import { FC, memo, ReactNode, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { getCustomerUser } from 'redux/reducer/user/getCustomerUser';
import { RootState, store } from 'redux/store';

export type sidebarSubmenu = {
  icon?: ReactNode;
  title: string;
  to: string;
  isDisable?: boolean;
};

interface ISidebarMenu {
  icon: ReactNode;
  title: string;
  to?: string;
  subMenu?: sidebarSubmenu[];
  isDisable?: boolean;
  isExternalHref?: boolean;
}

const noAccessMessage = `کاربر گرامی برای دسترسی به این قسمت، اطلاعات حساب کاربری خود را تایید و حداقل یک حساب بانکی ثبت نمایید.
              برای تایید اطلاعات حساب کاربری و افزودن حساب بانکی، روی باکس قرمز رنگ بالا سمت چپ، کلیک نمایید .`;

const SidebarMenu: FC<ISidebarMenu> = ({ icon, to, title, subMenu, isDisable, isExternalHref }) => {
  //hooks
  const { pathname } = useLocation();
  const { userProfile, customerUser } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    if (userProfile && !customerUser) store.dispatch(getCustomerUser(userProfile.id));
  }, [customerUser, userProfile]);

  //constants
  const renderSubMenus = useMemo(() => {
    return (
      <div>
        {isDisable
          ? subMenu?.map((menu) => (
              <TcTooltip key={menu.title + menu.to} title={noAccessMessage}>
                <TcButton type={isMenuSelected(menu.to, pathname) ? 'default' : 'text'} className='flex items-center justify-start w-full opacity-60 gap-x-1'>
                  {menu.icon && menu.icon}
                  <span>{menu.title}</span>
                </TcButton>
              </TcTooltip>
            ))
          : subMenu?.map((menu) => (
              <Link key={menu.title + menu.to} to={menu.to}>
                <TcButton type={isMenuSelected(menu.to, pathname) ? 'default' : 'text'} className='flex items-center justify-start w-full gap-x-1'>
                  {menu.icon && menu.icon}
                  <span>{menu.title}</span>
                </TcButton>
              </Link>
            ))}
      </div>
    );
  }, [pathname, isDisable, userProfile, customerUser]);

  const renderMenuBox = useMemo(() => {
    return (
      <div
        className={`${
          isDisable ? 'opacity-30' : ''
        } flex flex-col gap-1 items-center justify-center w-20 h-20 rounded-lg cursor-pointer border bg-t-bg-color hover:text-t-primary-color no-animation inherit-color 
        ${isAnySubMenusSelected(pathname, subMenu) ? 'border-t-primary-color' : 'border-transparent'}`}>
        <div className='text-2xl inherit-color '>{icon}</div>
        <span className='text-sm'>{title}</span>
      </div>
    );
  }, [pathname, isDisable, userProfile, customerUser]);

  const renderToMenu = useMemo(() => {
    if (!to) return <></>;
    if (isExternalHref)
      return (
        <a
          href={to}
          target='_blank'
          rel='noreferrer'
          className={`${
            isDisable ? 'opacity-30' : ''
          } flex flex-col items-center border gap-1 justify-center w-20 h-20 rounded-lg bg-t-bg-color hover:text-t-primary-color no-animation inherit-color ${
            isMenuSelected(to, pathname) ? 'border-t-primary-color' : 'border-transparent'
          }`}>
          <div className='text-2xl'>{icon}</div>

          <span className='text-sm'>{title}</span>
        </a>
      );
    if (isDisable)
      return (
        <TcTooltip placement='left' title={noAccessMessage}>
          <div
            className={`${
              isDisable ? 'opacity-30' : ''
            } flex flex-col items-center border gap-1 justify-center w-20 h-20 rounded-lg bg-t-bg-color hover:text-t-primary-color no-animation inherit-color ${
              isMenuSelected(to, pathname) ? 'border-t-primary-color' : 'border-transparent'
            }`}>
            <div className='text-2xl'>{icon}</div>

            <span className='text-sm'>{title}</span>
          </div>
        </TcTooltip>
      );

    return (
      <Link to={to}>
        <div
          className={`${
            isDisable ? 'opacity-30' : ''
          } flex flex-col items-center border gap-1 justify-center w-20 h-20 rounded-lg bg-t-bg-color hover:text-t-primary-color no-animation inherit-color ${
            isMenuSelected(to, pathname) ? 'border-t-primary-color' : 'border-transparent'
          }`}>
          <div className='text-2xl'>{icon}</div>

          <span className='text-sm'>{title}</span>
        </div>
      </Link>
    );
  }, [pathname, userProfile, customerUser]);

  if (to) return <>{renderToMenu}</>;

  return (
    <Popover content={renderSubMenus} placement='left'>
      {renderMenuBox}
    </Popover>
  );
};

export default memo(SidebarMenu);
